import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";
import {
  Badge,
  NumberInput,
  Select,
  Switch,
  TextInput,
  Textarea,
} from "@mantine/core";
import { IconPencil, IconTrash } from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import ActionCellRender from "../../components/actionCellRender";
import RemoteSelect2 from "../../components/remoteSelect2";
import { useServerApi } from "../../hooks/userServerApi";
import { notifyListRefresh } from "../../context/tabContext";

const apiEntity = "chartOfAccount";

const NORMAL_BALANCE_BY_TYPE = {
  ASSET: "DEBIT",
  LIABILITY: "CREDIT",
  EQUITY: "CREDIT",
  REVENUE: "CREDIT",
  EXPENSE: "DEBIT",
};

const AccountTypeSelect = ({ form, ...props }) => {
  const inputProps = form.getInputProps("type");
  return (
    <Select
      {...props}
      {...inputProps}
      onChange={(value) => {
        form.setFieldValue("type", value);
        if (NORMAL_BALANCE_BY_TYPE[value]) {
          form.setFieldValue("normalBalance", NORMAL_BALANCE_BY_TYPE[value]);
        }
      }}
    />
  );
};

const buildFormSchema = (t) => ({
  title: "accounting.chartOfAccount.title",
  titleField: "name",
  apiEntity,
  closeAfterSave: true,
  initialValues: {
    isActive: true,
    isControlAccount: false,
    sortOrder: 0,
  },
  display: {
    mode: ENUM_FORM_DISPLAY.MODAL,
    size: "lg",
  },
  layout: {
    containers: [
      {
        key: "main",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "accounting.chartOfAccount.accountDetails",
          description: "accounting.chartOfAccount.accountDetailsDesc",
        },
      },
    ],
    fields: [
      {
        name: "code",
        component: TextInput,
        parent: "main",
        props: {
          label: "Code",
          required: true,
          placeholder: "accounting.chartOfAccount.codePlaceholder",
        },
      },
      {
        name: "name",
        component: TextInput,
        parent: "main",
        props: {
          label: "Name",
          required: true,
        },
      },
      {
        name: "type",
        component: AccountTypeSelect,
        parent: "main",
        props: {
          label: "Type",
          required: true,
          data: [
            { value: "ASSET", label: t("accounting.accountType.asset") },
            { value: "LIABILITY", label: t("accounting.accountType.liability") },
            { value: "EQUITY", label: t("accounting.accountType.equity") },
            { value: "REVENUE", label: t("accounting.accountType.revenue") },
            { value: "EXPENSE", label: t("accounting.accountType.expense") },
          ],
          searchable: true,
        },
      },
      {
        name: "normalBalance",
        component: Select,
        parent: "main",
        props: {
          label: "Normal Balance",
          required: true,
          data: [
            { value: "DEBIT", label: t("accounting.normalBalance.debit") },
            { value: "CREDIT", label: t("accounting.normalBalance.credit") },
          ],
        },
      },
      {
        name: "parent",
        component: RemoteSelect2,
        parent: "main",
        props: {
          label: "Parent Account",
          apiEntity: "chartOfAccount",
          labelField: "code",
          labelRender: (item) => `${item.code} - ${item.name}`,
          searchFields: ["code", "name"],
          clearable: true,
          preQuery: { isActive: true },
        },
      },
      {
        name: "subledgerType",
        component: Select,
        parent: "main",
        props: {
          label: "Subledger Type",
          data: [
            { value: "AR_CLIENT", label: t("accounting.subledgerType.arClient") },
            { value: "AP_INSURER", label: t("accounting.subledgerType.apInsurer") },
            { value: "CASH_BANK", label: t("accounting.subledgerType.cashBank") },
            {
              value: "COMMISSION_RECEIVABLE",
              label: t("accounting.subledgerType.commissionReceivable"),
            },
            {
              value: "CONSULTANT_PAYABLE",
              label: t("accounting.subledgerType.consultantPayable"),
            },
            { value: "CLIENT_DEPOSIT", label: t("accounting.subledgerType.clientDeposit") },
          ],
          clearable: true,
        },
      },
      {
        name: "sortOrder",
        component: NumberInput,
        parent: "main",
        props: {
          label: "Sort Order",
          min: 0,
        },
      },
      {
        name: "description",
        component: Textarea,
        parent: "main",
        props: {
          label: "Description",
          minRows: 2,
        },
      },
      {
        name: "isControlAccount",
        component: Switch,
        parent: "main",
        props: {
          label: "Control account",
        },
      },
      {
        name: "isActive",
        component: Switch,
        parent: "main",
        props: {
          label: "accounting.field.active",
        },
      },
    ],
  },
});

const buildTableSchema = (t) => ({
  showActionColumn: true,
  columns: [
    {
      field: "code",
      headerName: "Code",
      sortable: true,
    },
    {
      field: "name",
      headerName: "Name",
      sortable: true,
    },
    {
      field: "type",
      headerName: "Type",
      sortable: true,
      cellRender: (col, data) => {
        const labels = {
          ASSET: t("accounting.accountType.asset"),
          LIABILITY: t("accounting.accountType.liability"),
          EQUITY: t("accounting.accountType.equity"),
          REVENUE: t("accounting.accountType.revenue"),
          EXPENSE: t("accounting.accountType.expense"),
        };
        return labels[data.type] || data.type;
      },
    },
    {
      field: "normalBalance",
      headerName: "Normal Balance",
      sortable: true,
      cellRender: (col, data) =>
        data.normalBalance === "DEBIT"
          ? t("accounting.normalBalance.debit")
          : data.normalBalance === "CREDIT"
            ? t("accounting.normalBalance.credit")
            : data.normalBalance,
    },
    {
      field: "parent",
      headerName: "Parent",
      sortable: false,
      cellRender: (col, data) =>
        data.parent ? `${data.parent.code} - ${data.parent.name}` : "",
    },
    {
      field: "isControlAccount",
      headerName: "Control",
      sortable: true,
      cellRender: (col, data) =>
        data.isControlAccount ? (
          <Badge size="xs" color="teal">
            {t("Yes")}
          </Badge>
        ) : null,
    },
    {
      field: "isActive",
      headerName: "accounting.field.active",
      sortable: true,
      cellRender: (col, data) => (
        <Badge size="xs" color={data.isActive ? "green" : "gray"}>
          {data.isActive ? t("Yes") : t("No")}
        </Badge>
      ),
    },
    {
      field: "action",
      cellRender: ActionCellRender,
      isAction: true,
      actions: [
        {
          name: "edit",
          icon: IconPencil,
        },
      ],
      actionMenu: [
        {
          name: "delete",
          label: "accounting.chartOfAccount.deleteAccount",
          icon: IconTrash,
          props: {
            color: "red",
          },
        },
      ],
    },
  ],
  searchableFields: ["code", "name", "description"],
});

const ChartOfAccounts = () => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [seeding, setSeeding] = useState(false);
  const [hasAccounts, setHasAccounts] = useState(false);
  const location = useLocation();

  const formSchema = useMemo(() => buildFormSchema(t), [t]);
  const tableSchema = useMemo(() => buildTableSchema(t), [t]);

  const customActions = useMemo(
    () =>
      hasAccounts
        ? []
        : [
            {
              label: seeding
                ? t("accounting.chartOfAccount.seeding")
                : t("accounting.chartOfAccount.seedTemplate"),
              variant: "default",
              disabled: seeding,
              requiresWrite: true,
              onClick: async () => {
                setSeeding(true);
                try {
                  await api.Accounting.seedChartOfAccounts();
                  showNotification({
                    title: t("accounting.chartOfAccount.seedSuccessTitle"),
                    message: t("accounting.chartOfAccount.seedSuccessMessage"),
                  });
                  notifyListRefresh(location.pathname);
                } catch (error) {
                  showNotification({
                    color: "red",
                    title: t("accounting.chartOfAccount.seedFailed"),
                    message: String(error?.message || error),
                  });
                } finally {
                  setSeeding(false);
                }
              },
            },
          ],
    [api, hasAccounts, seeding, location.pathname, t]
  );

  return (
    <PageList
      title={t("accounting.modules.chartOfAccounts.label")}
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={formSchema}
      customActions={customActions}
      onFetchSuccess={(total) => setHasAccounts(total > 0)}
      initSort={{ by: "code", order: "asc" }}
      defaultPageSize={100}
    />
  );
};

export default ChartOfAccounts;
